import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Ticket, Plus, Search } from 'lucide-react';
import { complaints, sites, districts } from '../mock';
import { toast } from '../hooks/use-toast';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Complaints = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);

  const filteredComplaints = complaints.filter(c => {
    const matchesSearch = c.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         c.site.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleCreateTicket = (e) => {
    e.preventDefault();
    toast({
      title: 'Ticket Created',
      description: 'New complaint ticket has been created successfully.',
    });
    setDialogOpen(false);
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return { bg: '#fee', color: '#e74c3c', border: '#e74c3c' };
    if (priority === 'medium') return { bg: '#fff4e6', color: '#f39c12', border: '#f39c12' };
    return { bg: '#e8f5f1', color: '#10ac84', border: '#10ac84' };
  };

  const getStatusColor = (status) => {
    if (status === 'resolved') return { bg: '#e8f5f1', color: '#10ac84', border: '#10ac84' };
    if (status === 'in-progress') return { bg: '#e3f2fd', color: '#3498db', border: '#3498db' };
    return { bg: '#fff4e6', color: '#f39c12', border: '#f39c12' };
  };

  const getSLAColor = (sla) => {
    if (sla === 'overdue') return { bg: '#fee', color: '#e74c3c' };
    return { bg: '#e8f5f1', color: '#10ac84' };
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Consumer Complaints</h1>
          <p className="text-base mt-2" style={{ color: '#576574' }}>Service ticket management and tracking</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: '#10ac84' }}>
              <Plus className="w-4 h-4 mr-2" />
              Create New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Complaint Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-6">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="device">Device Malfunction</SelectItem>
                    <SelectItem value="generation">Low Generation</SelectItem>
                    <SelectItem value="maintenance">Maintenance Request</SelectItem>
                    <SelectItem value="consumer">Consumer Complaint</SelectItem>
                    <SelectItem value="data">Data Sync Issue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Site</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>{site.id} - {site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea placeholder="Describe the issue in detail..." rows={5} />
              </div>
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="team-a">Tech Team A</SelectItem>
                    <SelectItem value="team-b">Tech Team B</SelectItem>
                    <SelectItem value="team-c">Tech Team C</SelectItem>
                    <SelectItem value="maintenance">Maintenance Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" style={{ backgroundColor: '#10ac84' }}>Create Ticket</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{complaints.length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Total Tickets</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#f39c12' }}>{complaints.filter(c => c.status === 'open').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Open</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#3498db' }}>{complaints.filter(c => c.status === 'in-progress').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>In Progress</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-3xl font-bold mb-2" style={{ color: '#10ac84' }}>{complaints.filter(c => c.status === 'resolved').length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Resolved</div>
          </CardContent>
        </Card>
      </div>

      {/* Complaint Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Pie
                data={{
                  labels: ['Open', 'In Progress', 'Resolved'],
                  datasets: [{
                    data: [
                      complaints.filter(c => c.status === 'open').length,
                      complaints.filter(c => c.status === 'in-progress').length,
                      complaints.filter(c => c.status === 'resolved').length
                    ],
                    backgroundColor: [
                      'rgba(243, 156, 18, 0.8)',
                      'rgba(52, 152, 219, 0.8)',
                      'rgba(16, 172, 132, 0.8)'
                    ],
                    borderColor: [
                      '#f39c12',
                      '#3498db',
                      '#10ac84'
                    ],
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Priority Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar
                data={{
                  labels: ['High', 'Medium', 'Low'],
                  datasets: [{
                    label: 'Tickets',
                    data: [
                      complaints.filter(c => c.priority === 'high').length,
                      complaints.filter(c => c.priority === 'medium').length,
                      complaints.filter(c => c.priority === 'low').length
                    ],
                    backgroundColor: [
                      'rgba(231, 76, 60, 0.8)',
                      'rgba(243, 156, 18, 0.8)',
                      'rgba(16, 172, 132, 0.8)'
                    ],
                    borderColor: [
                      '#e74c3c',
                      '#f39c12',
                      '#10ac84'
                    ],
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1,
                        color: '#576574',
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#576574' }} />
              <Input
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <div className="space-y-4">
        {filteredComplaints.map((complaint) => (
          <Card key={complaint.id} onClick={() => navigate(`/site/${complaint.site}`)} className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                    <Ticket className="w-6 h-6" style={{ color: '#10ac84' }} />
                  </div>
                  <div>
                    <div className="font-mono text-lg font-bold mb-1" style={{ color: '#0a3d62' }}>{complaint.id}</div>
                    <div className="text-sm" style={{ color: '#576574' }}>Created: {complaint.createdAt}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" style={getPriorityColor(complaint.priority)}>
                    {complaint.priority.toUpperCase()}
                  </Badge>
                  <Badge variant="outline" style={getStatusColor(complaint.status)}>
                    {complaint.status.toUpperCase()}
                  </Badge>
                  {complaint.sla && (
                    <Badge variant="outline" style={getSLAColor(complaint.sla)}>
                      SLA: {complaint.sla.toUpperCase()}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <div className="text-sm mb-1" style={{ color: '#576574' }}>Category</div>
                  <div className="font-semibold">{complaint.category}</div>
                </div>
                <div>
                  <div className="text-sm mb-1" style={{ color: '#576574' }}>Site</div>
                  <div className="font-semibold font-mono">{complaint.site}</div>
                </div>
                <div>
                  <div className="text-sm mb-1" style={{ color: '#576574' }}>District</div>
                  <div className="font-semibold capitalize">{districts.find(d => d.id === complaint.district)?.name}</div>
                </div>
                <div>
                  <div className="text-sm mb-1" style={{ color: '#576574' }}>Assigned To</div>
                  <div className="font-semibold">{complaint.assignedTo}</div>
                </div>
              </div>
              <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: '#f5f7fa' }}>
                <div className="text-sm mb-1" style={{ color: '#576574' }}>Description</div>
                <div>{complaint.description}</div>
              </div>
              {complaint.resolvedAt && (
                <div className="text-sm" style={{ color: '#10ac84' }}>Resolved: {complaint.resolvedAt}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Complaints;
